import { useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Sparkles, Loader2, AlertCircle } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

// Reusable dialog that renders an AI result (loading / error / empty / content + sources).
export function AiResultDialog({ open, onOpenChange, title, loading, error, content, sources, empty }) {
  const { t } = useLang();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm sm:max-w-2xl" data-testid="ai-result-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm">
            <Sparkles size={16} className="text-primary" /> {title}
          </DialogTitle>
          <DialogDescription className="text-[11px]">{t("ai.disclaimer")}</DialogDescription>
        </DialogHeader>
        {loading && (
          <div className="flex items-center gap-2 py-10 text-sm text-zinc-500" data-testid="ai-loading">
            <Loader2 className="animate-spin" size={18} /> {t("ai.thinking")}
          </div>
        )}
        {!loading && error && (
          <div className="flex items-start gap-2 rounded-sm border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700" data-testid="ai-error">
            <AlertCircle size={16} className="mt-0.5 shrink-0" /> {error}
          </div>
        )}
        {!loading && !error && empty && (
          <div className="py-6 text-sm text-zinc-500" data-testid="ai-empty">{t("ai.noData")}</div>
        )}
        {!loading && !error && !empty && content && (
          <div className="max-h-[60vh] overflow-y-auto pr-1">
            <div className="whitespace-pre-wrap text-sm leading-relaxed text-zinc-800" data-testid="ai-content">{content}</div>
            {sources?.length > 0 && (
              <div className="mt-4 border-t border-zinc-100 pt-3" data-testid="ai-sources">
                <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-zinc-400">{t("ai.sources")}</div>
                <div className="flex flex-wrap gap-1.5">
                  {sources.map((sc, i) => (
                    <span key={i} className="inline-flex items-center border border-zinc-200 bg-zinc-50 px-2 py-0.5 font-mono text-[10px] text-zinc-600">{sc.file}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Hook: open the dialog and POST an AI request, tracking loading/error/result.
export function useAiRunner() {
  const [state, setState] = useState({ open: false, loading: false, error: "", content: "", sources: [], empty: false, title: "" });
  const run = useCallback(async (title, path, body) => {
    setState({ open: true, loading: true, error: "", content: "", sources: [], empty: false, title });
    try {
      if (["/ai/summarize-actions", "/ai/explain-drift"].includes(path) && !(body && body.items)) {
        localStorage.setItem("lastAiAction", JSON.stringify({ title, path, body, ts: Date.now() }));
        window.dispatchEvent(new CustomEvent("ai-action-recorded"));
      }
    } catch { /* ignore storage errors */ }
    try {
      const { data } = await api.post(path, body);
      setState({ open: true, loading: false, error: "", content: data.content || "", sources: data.sources || [], empty: !!data.empty, title });
    } catch (e) {
      setState({ open: true, loading: false, error: apiErr(e), content: "", sources: [], empty: false, title });
    }
  }, []);
  const close = useCallback(() => setState((s) => ({ ...s, open: false })), []);
  return { state, run, close };
}
